import { useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import type { CampaignFilters, CampaignType, Recipient } from "../types/ICampaign";
import { previewRecipientCount, previewRecipients } from "../api/campaigns";

export function useRecipientPreview(
  filters: CampaignFilters,
  campaignType: CampaignType,
  isOpen: boolean,
  activeTab: "filters" | "recipients"
) {
  const [debouncedFilters, setDebouncedFilters] =
    useState<CampaignFilters>(filters);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [filters]);

  const { data: countData, isLoading: isCountLoading } = useQuery(
    ["preview-recipient-count", debouncedFilters, campaignType],
    () => previewRecipientCount(debouncedFilters, campaignType),
    { enabled: isOpen }
  );

  const { data: recipientsData, isLoading: isRecipientsLoading } =
    useQuery<Recipient[]>(
      ["preview-recipients", debouncedFilters, campaignType],
      () => previewRecipients(debouncedFilters, campaignType),
      { enabled: isOpen && activeTab === "recipients" }
    );

  return {
    liveCount: countData?.count ?? null,
    isLiveCountLoading: isCountLoading,
    recipients: recipientsData || [],
    isRecipientsLoading,
  };
}
